<template>
  <v-dialog :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields['title'] }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text class="pa-4">
        <div>
          <label class="label-input">Section Name</label>
          <v-row>
            <v-col cols="12" md="8">
              <div>
                <v-text-field v-model="fields['title']" label="Name" />
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox small v-model="fields['enableAnchor']" label="Enable Anchor" />
            </v-col>
          </v-row>
        </div>
        <label class="label-input">Header</label>
        <ckeditor :editor="editor" v-model="fields['titleHeader']" :config="editorConfig" />
        <div class="mt-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <label class="label-input mb-0" style="margin-bottom: 0">Media Items</label>
            <v-btn small color="primary" @click="handleAddMedia">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Media</span>
            </v-btn>
          </div>
          <div class="group-item">
            <div>
              <p class="text-caption">
                Slides Per View. The viewport is based on the description's width, not the window's width.
              </p>
              <v-row>
                <v-col cols="12" md="4">
                  <div>
                    <v-text-field
                      v-model.number="fields.breakpoints.mobile.slidesPerView"
                      label="Mobile"
                      type="number"
                      :rules="[v => v > 0 || 'Slides Per View must be greater than 0']"
                      outlined
                      dense
                    ></v-text-field>
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div>
                    <v-text-field
                      v-model.number="fields.breakpoints.tablet.slidesPerView"
                      label="Tablet"
                      type="number"
                      :rules="[v => v > 0 || 'Slides Per View must be greater than 0']"
                      outlined
                      dense
                    ></v-text-field>
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div>
                    <v-text-field
                      v-model.number="fields.breakpoints.desktop.slidesPerView"
                      label="Desktop"
                      type="number"
                      :rules="[v => v > 0 || 'Slides Per View must be greater than 0']"
                      outlined
                      dense
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </div>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  type="number"
                  v-model.number="fields.containerWidth"
                  label="Container Width"
                  min="0"
                  suffix="%"
                  dense
                  :rules="[v => (v >= 0 && v <= 100) || 'Container Width must be between 0 and 100']"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-checkbox v-model="fields.showRating" label="Show Rating" dense />
              </v-col>
            </v-row>
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, i) in fields['listItem']" :key="i">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <span>{{ item.type === 'image' ? 'Image' : 'Video' }} {{ i + 1 }}</span>
                    <v-btn icon @click.stop="handleRemoveMedia(i)">
                      <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="mb-4">
                    <div style="margin-bottom: 8px">Media Type</div>
                    <v-radio-group v-model="item.type" row>
                      <v-radio label="Image" value="image"></v-radio>
                      <v-radio label="Video" value="video"></v-radio>
                    </v-radio-group>
                  </div>

                  <div class="mb-4">
                    <div style="margin-bottom: 8px">Rating</div>
                    <v-rating
                      color="warning"
                      background-color="#c1c1c1"
                      empty-icon="mdi-star-outline"
                      full-icon="mdi-star"
                      hover
                      length="5"
                      size="28"
                      :value="item.rating"
                      @input="val => (item.rating = val)"
                    />
                  </div>

                  <!-- Image Upload -->
                  <div v-if="item.type === 'image'">
                    <div class="mb-4">
                      <div style="margin-bottom: 8px">Image</div>
                      <UploadImage
                        @callback="obj => handleUploadImage(obj, i)"
                        :image="{ imageSrc: item.image }"
                        :height="`200`"
                        :typeUpload="'image'"
                        @callbackDelete="handleRemoveImage(i)"
                      />
                    </div>
                    <div class="mt-8">
                      <div style="margin-bottom: 8px !important">Content</div>
                      <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
                    </div>
                  </div>

                  <!-- Video URL -->
                  <div v-if="item.type === 'video'">
                    <div class="d-flex align-center gap-6 mb-4">
                      <v-text-field v-model="item.video" label="Video URL" />
                      <v-btn @click="handlePreviewVideo(i)" text color="primary">
                        <v-icon>mdi-reload</v-icon> Reload
                      </v-btn>
                    </div>
                    <div class="video-wrapper">
                      <div class="wrap-video">
                        <video width="100%" height="auto" autoplay muted loop playsinline :ref="'videoPlayer'">
                          <source data-attribute="card-video" type="video/mp4" :src="item.video" />
                        </video>
                      </div>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
        <div class="mt-6">
          <label class="label-input">Section Settings</label>
          <div class="group-item">
            <div>
              <div class="font-weight-medium">Spacing</div>
              <v-row>
                <v-col cols="6">
                  <v-checkbox v-model="fields.topPadding" label="Top Spacing" dense hide-details />
                </v-col>
                <v-col cols="6">
                  <v-checkbox v-model="fields.bottomPadding" label="Bottom Spacing" dense hide-details />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Container Colors</div>
              <v-row>
                <v-col cols="12" md="4">
                  <ColorPicker
                    v-model="fields.colors.backgroundColor"
                    name="backgroundColor"
                    label="Background Color"
                    default-color="#FFFFFF"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <ColorPicker
                    v-model="fields.colors.textColor"
                    name="textColor"
                    label="Text Color"
                    default-color="#000000"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <ColorPicker
                    v-model="fields.colors.starColor"
                    name="starColor"
                    label="Star Color"
                    default-color="#14a16c"
                  />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3">Pagination Colors</div>
              <v-row>
                <v-col cols="12" md="6">
                  <ColorPicker
                    v-model="fields.colors.paginationActiveColor"
                    name="paginationActiveColor"
                    label="Active Color"
                    default-color="#000000"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <ColorPicker
                    v-model="fields.colors.paginationInactiveColor"
                    name="paginationInactiveColor"
                    label="Inactive Color"
                    default-color="#cccccc"
                  />
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '@/views/website/products/components/description/common/html/update';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { replaceImageUrl } from '../common/utils';
import ColorPicker from './common/color-picker.vue';

export default {
  name: 'ReviewGrid05',
  components: {
    UploadImage,
    ColorPicker,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: '',
        topPadding: true,
        bottomPadding: true,
        titleHeader: '',
        breakpoints: {
          mobile: {
            slidesPerView: 1,
          },
          tablet: {
            slidesPerView: 2,
          },
          desktop: {
            slidesPerView: 2,
          },
        },
        listItem: [],
        colors: {
          backgroundColor: '',
          textColor: '',
          starColor: '',
          paginationActiveColor: '',
          paginationInactiveColor: '',
        },
        containerWidth: 75,
        showRating: true,
      },
    };
  },
  mounted() {
    this.fields = {
      ...this.fields,
      ...this.sectionEditor.sectionExtract,
    };
  },
  methods: {
    handlePreviewVideo(index) {
      const video = this.$refs.videoPlayer[index];
      if (video) {
        video.load();
        video.play();
      }
    },
    handleAddMedia() {
      const imageUrl = replaceImageUrl(
        'https://cdn.skyfastimg.com/images/2026/04/08/image-69d60cee3fc70a177e2bc484.png?w=500&h=50&q=80',
      );
      this.fields['listItem'].push({
        type: 'image',
        video: '',
        image: imageUrl,
        content: `<p style="text-align: center"> <i> <strong>Suki,</strong> </i> <span> <i> <strong> Switzerland</strong> </i> </span> </p> <p style="text-align: center"> <span>&nbsp;✅ </span> <span style="color: rgb(20, 161, 108)"> <strong>VERIFIED BUYER</strong> </span> </p> <h3 style="text-align: center"> <span> <strong>"My dog loves the taste and Keeps asking for more!"</strong> </span> </h3>`,
        rating: 5,
      });
    },
    handleRemoveMedia(index) {
      this.fields['listItem'].splice(index, 1);
    },
    handleUploadImage(obj, index) {
      this.fields['listItem'][index].image = obj.imageSrc;
    },
    handleRemoveImage(index) {
      this.fields['listItem'][index].image = '';
    },
    handleChangeBackgroundColor(event) {
      this.fields['background-color'] = event.target.value;
    },
    handleResetBackgroundColor() {
      this.fields['background-color'] = '#ffffff';
    },
    handleChangePaginationActiveColor(event) {
      this.fields['pagination-active-color'] = event.target.value;
    },
    handleChangePaginationInactiveColor(event) {
      this.fields['pagination-inactive-color'] = event.target.value;
    },
    handleResetPaginationActiveColor() {
      this.fields['pagination-active-color'] = '#000000';
    },
    handleResetPaginationInactiveColor() {
      this.fields['pagination-inactive-color'] = '#cccccc';
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionReviewGrid05(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields['title'],
        html: updateHtml,
      });
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .ck-editor__editable_inline {
  min-height: 100px !important;
}
::v-deep .ck-editor__editable {
  min-height: 100px !important;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
.video-wrapper {
  background: rgba(0, 0, 0, 0.767);
  .wrap-video {
    max-width: 300px;
    margin: 0 auto;
    video {
      display: block;
      width: 100%;
      height: auto;
    }
  }
}

.breakpoint-label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  display: block;
}
</style>
